import { useState, type ReactNode } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { useT } from '../i18n/index.js';
import { pestanaDe, permitida } from '../lib/secciones.js';
import { usePermisos } from '../lib/sesion.js';
import { llegaConTransicion } from '../lib/transiciones.js';
import { alVolver, atrasHasta } from '../lib/volver.js';
import { Ico, type NombreIcono } from './Iconos.js';
import { Pestanas } from './Pestanas.js';

interface Props {
  titulo: string;
  subtitulo?: string;
  /**
   * Where «Volver» goes when nothing on this page load came before this screen: opened from a
   * link outside, or the first after a reload. Otherwise it goes back the way it came.
   */
  volverA?: string;
  /** And with what search: a list opened on one of its views. */
  volverBusqueda?: Record<string, unknown>;
  acciones?: ReactNode;
  children: ReactNode;
  /** Wide layouts (bulk entry, purchase lines) need the room. */
  ancho?: 'normal' | 'amplio';
}

export function Pagina({
  titulo,
  subtitulo,
  volverA = '/',
  volverBusqueda,
  acciones,
  children,
  ancho = 'normal',
}: Props) {
  const puede = usePermisos();
  // Arriving by a view transition, the transition is the entrance; otherwise the old fade.
  const [entrada] = useState(() => (llegaConTransicion() ? '' : 'aparece'));
  const aqui = useLocation({ select: (l) => l.pathname });

  // A tab of a section shows its neighbours.
  const pestanas = pestanaDe(aqui)?.seccion.pestanas.filter((p) => permitida(p, puede)) ?? [];

  return (
    <div
      className={`${entrada} vt-pagina mx-auto w-full ${ancho === 'amplio' ? 'max-w-6xl' : 'max-w-2xl'} p-5 pb-24`}
    >
      {pestanas.length > 1 && <Pestanas pestanas={pestanas} actual={aqui} />}
      {/*
        The header wraps, in both directions.

        It used to be one row with `shrink-0` on the actions, which is a promise the screen
        cannot keep: Productos carries four of them, and at 420px they ran 46px off the right
        edge — before any of them had an icon. Icons made it 112px. `shrink-0` does not make
        room, it just decides who gets pushed out of the viewport.

        So the title gets a floor (`basis-40`) rather than a fixed share, and when the actions
        no longer fit beside it they take a line of their own; when there are too many for one
        line they wrap among themselves. Nothing is ever off-screen, at any width.
      */}
      <header className="mb-5 flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        {/*
          No `min-w-0` on the name's side: it is as wide as its longest word at least, so on a
          narrow phone «Volver» drops below the name before the house can push «Acetaminofén» into
          breaking inside the word.
        */}
        <div className="flex flex-1 basis-40 items-start gap-3">
          <BotonInicio />
          <div className="min-w-0 flex-1 self-center">
            {/*
              Two lines before anything is cut: on a 320 px phone a product's page lost the part of
              its name that tells two products apart, «Acetaminofén 5…». Balanced, so the break
              falls between words.
            */}
            <h1 className="line-clamp-2 text-xl font-bold text-balance wrap-break-word">{titulo}</h1>
            {subtitulo && <p className="mt-0.5 text-sm text-slate-400">{subtitulo}</p>}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {acciones}
          <BotonVolver
            a={volverA}
            {...(volverBusqueda ? { busqueda: volverBusqueda } : {})}
            className="btn-secundario h-11 min-h-0 gap-1.5 pr-4 pl-3 text-sm"
          />
        </div>
      </header>
      {children}
    </div>
  );
}

/**
 * «Volver»: back the way this screen was come to, through the history, as the phone's back button
 * goes (`lib/volver.ts`). The link's own address, `a`, is where it goes when nothing came before
 * on this page load, and it takes this screen's place there, so that its own «Volver» does not
 * lead back here. One to a screen, so the lessons find it by the same name on every one.
 */
export function BotonVolver({
  a,
  busqueda,
  className,
  children,
}: {
  a: string;
  busqueda?: Record<string, unknown>;
  className: string;
  children?: ReactNode;
}) {
  const t = useT();
  return (
    <Link
      to={a}
      {...(busqueda ? { search: busqueda } : {})}
      replace
      onClick={alVolver}
      className={className}
      data-guia="pagina-volver"
    >
      {children ?? (
        <>
          <Ico icono="atras" className="size-4" />
          {t('comun.volver')}
        </>
      )}
    </Link>
  );
}

/**
 * La casita: straight to the home screen, however many «Volver» away it is.
 *
 * Asked for by the owner (2026-10-02). From a tab of a section opened from the menu, or a form
 * opened from its list, home was two or three «Volver» away. It is on every screen that has a
 * «Volver», even where «Volver» itself goes home, so it is always found in the same place: before
 * the screen's name, where an app keeps its way up. «Volver» and the screen's own buttons keep the
 * places they had, and the header its height, a two-word name taking two lines where it took one,
 * and a long word taking a line of its own above «Volver» rather than breaking. On a phone it is
 * the house alone, since the header has no room for another word; the word comes back on a wider
 * screen, and is always there for a screen reader. The home screen it was opened from is gone back
 * to rather than opened again on top, so the phone's back button there does not step back through
 * what was just left.
 */
export function BotonInicio({ className = '' }: { className?: string }) {
  const t = useT();
  return (
    <Link
      to="/"
      onClick={atrasHasta('/')}
      title={t('pos.volverInicio')}
      className={`btn-secundario h-11 min-h-0 min-w-11 shrink-0 gap-1.5 px-3 text-sm ${className}`}
    >
      <Ico icono="inicio" className="size-4" />
      <span className="max-sm:sr-only">{t('comun.inicio')}</span>
    </Link>
  );
}

/**
 * Nothing here yet — said with a picture as well as a sentence.
 *
 * An empty screen that is only a line of grey text reads as broken. The icon is what tells
 * somebody at a glance that they are looking at a working screen with nothing in it, which is
 * a different thing from a screen that failed to load.
 */
export function Vacio({
  mensaje,
  icono = 'vacio',
  children,
}: {
  mensaje: string;
  icono?: NombreIcono;
  children?: ReactNode;
}) {
  return (
    <div className="tarjeta flex flex-col items-center gap-4 py-12 text-center">
      <span className="chip chip-neutro size-16 rounded-2xl">
        <Ico icono={icono} className="size-8" />
      </span>
      <p className="max-w-sm text-sm text-slate-400">{mensaje}</p>
      {children}
    </div>
  );
}
